import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';

const Leaderboard = () => {
  const navigate = useNavigate();
  const [todayLeaderboard, setTodayLeaderboard] = useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('today');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([{ name: 'all', displayName: 'All Categories' }]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchLeaderboards = useCallback(async (categoryValueParam) => {
    try {
      setLoading(true);
      setError(null);
      const categoryValue = typeof categoryValueParam === 'string' ? categoryValueParam : 'all';
      const params = {};
      if (categoryValue && categoryValue !== 'all') {
        params.category = categoryValue;
      }
      
      // Fetch today's leaderboard
      const todayResponse = await axios.get(`${API_BASE_URL}/api/quiz/leaderboard/today`, { params });
      console.log('Today leaderboard response:', todayResponse.data);
      setTodayLeaderboard(todayResponse.data || []);
      
      // Fetch all-time leaderboard
      const allTimeResponse = await axios.get(`${API_BASE_URL}/api/quiz/leaderboard/alltime`, { params });
      console.log('All-time leaderboard response:', allTimeResponse.data);
      setAllTimeLeaderboard(allTimeResponse.data || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setError('Failed to load leaderboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/quiz/categories`);
        const categoryOptions = Array.isArray(response.data) && response.data.length > 0
          ? response.data
          : [{ name: 'all', displayName: 'All Categories' }];

        setCategories(categoryOptions);

        const defaultCategory = categoryOptions.find(cat => cat.name === 'all')
          ? 'all'
          : categoryOptions[0].name;

        setSelectedCategory(defaultCategory);
        await fetchLeaderboards(defaultCategory);
      } catch (catError) {
        console.error('Error fetching categories:', catError);
        const fallbackCategories = [{ name: 'all', displayName: 'All Categories' }];
        setCategories(fallbackCategories);
        setSelectedCategory('all');
        await fetchLeaderboards('all');
      }
    };

    loadInitialData();
  }, [fetchLeaderboards]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRankEmoji = (rank) => {
    switch (rank) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏅';
    }
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'text-yellow-600 bg-yellow-50';
      case 2: return 'text-gray-600 bg-gray-50';
      case 3: return 'text-orange-600 bg-orange-50';
      default: return 'text-blue-600 bg-blue-50';
    }
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setSelectedCategory(value);
    fetchLeaderboards(value);
  };

  const currentCategoryLabel = categories.find(cat => cat.name === selectedCategory)?.displayName || 'All Categories';
  const isAllCategory = selectedCategory === 'all';

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleTakeQuiz = () => {
    navigate('/daily-quiz');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="text-red-600 text-lg font-medium mb-2">Error</div>
            <p className="text-red-600 mb-4">{error}</p>
            <div className="space-x-3">
              <button
                onClick={() => fetchLeaderboards(selectedCategory)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={handleBackToHome}
                className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="sm:hidden">Back</span>
                <span className="hidden sm:inline">Back to Home</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header with Navigation */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={handleBackToHome}
            className="inline-flex items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all"
          >
            <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Home</span>
          </button>
          
          <button
            onClick={() => fetchLeaderboards(selectedCategory)}
            disabled={loading}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🏆 Quiz Leaderboard</h1>
          <p className="text-gray-600">See how you rank among other quiz participants</p>
        </div>

        {/* Category Filter */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm sm:text-base text-gray-700">
            Viewing leaderboard for <span className="font-semibold text-gray-900">{currentCategoryLabel}</span>
          </div>
          <div className="flex items-center space-x-2">
            <label htmlFor="leaderboard-category" className="text-sm text-gray-600">Category</label>
            <select
              id="leaderboard-category"
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map((categoryOption) => (
                <option key={categoryOption.name} value={categoryOption.name}>
                  {categoryOption.displayName || categoryOption.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex mb-6 bg-white rounded-lg p-1 shadow-sm">
          <button
            onClick={() => setActiveTab('today')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'today'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Today's Rankings
          </button>
          <button
            onClick={() => setActiveTab('alltime')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'alltime'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            All-Time Rankings
          </button>
        </div>

        {/* Leaderboard Content */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          {activeTab === 'today' ? (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-800">Today's Top Performers</h2>
              {todayLeaderboard.length > 0 ? (
                <div className="space-y-3">
                  {todayLeaderboard.map((participant, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                        index < 3 ? getRankColor(index + 1) : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl">
                          {getRankEmoji(index + 1)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">
                            {participant.userName || 'Anonymous'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Rank #{index + 1}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg text-gray-800">
                          {participant.score}/{participant.totalQuestions || 10}
                        </div>
                        <div className="text-sm text-gray-600">
                          {Math.round((participant.score / (participant.totalQuestions || 10)) * 100)}%
                        </div>
                        {participant.timeSpent && (
                          <div className="text-xs text-gray-500">
                            Time: {formatTime(participant.timeSpent)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📝</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {isAllCategory ? 'No Quiz Results Yet Today' : `No ${currentCategoryLabel} Results Yet Today`}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {isAllCategory
                      ? "Be the first to take today's quiz and claim the top spot!"
                      : `Be the first to take a ${currentCategoryLabel} quiz today and claim the top spot!`
                    }
                  </p>
                  <div className="space-x-4">
                    <button
                      onClick={handleTakeQuiz}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Take Today's Quiz
                    </button>
                    <button
                      onClick={handleBackToHome}
                      className="bg-gray-600 text-white px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-base"
                    >
                      <span className="sm:hidden">Back</span>
                      <span className="hidden sm:inline">Back to Home</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-gray-800">All-Time Champions</h2>
              {allTimeLeaderboard.length > 0 ? (
                <div className="space-y-3">
                  {allTimeLeaderboard.map((participant, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                        index < 3 ? getRankColor(index + 1) : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl">
                          {getRankEmoji(index + 1)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800">
                            {participant.userName || 'Anonymous'}
                          </div>
                          <div className="text-sm text-gray-600">
                            Rank #{index + 1}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg text-gray-800">
                          {Math.round(participant.averageScore)}% avg
                        </div>
                        <div className="text-sm text-gray-600">
                          {participant.totalQuizzes} quiz{participant.totalQuizzes !== 1 ? 'es' : ''} completed
                        </div>
                        <div className="text-xs text-gray-500">
                          Best: {participant.bestScore}/{participant.totalQuestions || 10}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {isAllCategory ? 'Leaderboard Coming Soon' : `${currentCategoryLabel} Leaderboard Coming Soon`}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {isAllCategory
                      ? 'Complete quizzes to see rankings appear here!'
                      : `Complete ${currentCategoryLabel} quizzes to see rankings appear here!`
                    }
                  </p>
                  <div className="space-x-4">
                    <button
                      onClick={handleTakeQuiz}
                      className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Take a Quiz
                    </button>
                    <button
                      onClick={handleBackToHome}
                      className="bg-gray-600 text-white px-3 py-1.5 sm:px-6 sm:py-2 rounded-lg hover:bg-gray-700 transition-colors text-xs sm:text-base"
                    >
                      <span className="sm:hidden">Back</span>
                      <span className="hidden sm:inline">Back to Home</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bottom Navigation */}
        <div className="flex justify-center space-x-4 mt-8">
          <button
            onClick={() => fetchLeaderboards(selectedCategory)}
            disabled={loading}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-sm"
          >
            🔄 {loading ? 'Refreshing...' : 'Refresh Leaderboard'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
